import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";
import { fmtWIB, fmtDuration } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, Columns3, Search, ChevronLeft, ChevronRight, Eye, Ban, Archive, RotateCcw, Trash2, AlertTriangle } from "lucide-react";
import ResponseDetail from "@/components/ResponseDetail";
import { responseLanguageLabel, responseLanguageName } from "@/lib/responseLanguage";

const ALL_COLUMNS = [
  { key: "select", label: "", sticky: true, width: 40 },
  { key: "anonymous_code", label: "Kode", stickyEn: "Code", sticky: true, width: 110, default: true },
  { key: "instrument_name_id", label: "Instrumen", stickyEn: "Instrument", width: 180, default: true },
  { key: "instrument_version", label: "Versi", labelEn: "Version", width: 70 },
  { key: "status", label: "Status", width: 110, default: true },
  { key: "language", label: "Bahasa", labelEn: "Language", width: 110, default: true },
  { key: "name", label: "Nama", labelEn: "Name", width: 140 },
  { key: "email", label: "Email", width: 180 },
  { key: "age", label: "Usia", labelEn: "Age", width: 60 },
  { key: "gender", label: "Gender", width: 100 },
  { key: "education", label: "Pendidikan", labelEn: "Education", width: 130 },
  { key: "occupation", label: "Pekerjaan", labelEn: "Occupation", width: 130 },
  { key: "province", label: "Provinsi", labelEn: "Province", width: 130 },
  { key: "city", label: "Kota", labelEn: "City", width: 130 },
  { key: "started_at", label: "Mulai", labelEn: "Started", width: 160 },
  { key: "completed_at", label: "Selesai", labelEn: "Completed", width: 160, default: true },
  { key: "duration_seconds", label: "Durasi", labelEn: "Duration", width: 90 },
  { key: "total_score", label: "Skor", labelEn: "Score", width: 80, default: true },
  { key: "overall_category", label: "Kategori", labelEn: "Category", width: 130, default: true },
  { key: "dominant_dimension", label: "Dominan", labelEn: "Dominant", width: 130 },
  { key: "is_excluded", label: "Excluded", width: 90, default: true },
  { key: "is_archived", label: "Arsip", labelEn: "Archived", width: 90 },
  { key: "actions", label: "Aksi", labelEn: "Actions", sticky: "right", width: 160, default: true },
];

const EXCLUDE_REASONS = [
  { value: "duplicate", label_id: "Duplikat", label_en: "Duplicate" },
  { value: "incomplete", label_id: "Tidak lengkap / tidak valid", label_en: "Incomplete or invalid" },
  { value: "testing", label_id: "Respons pengujian", label_en: "Testing response" },
  { value: "suspicious", label_id: "Pola respons mencurigakan", label_en: "Suspicious pattern" },
  { value: "no_consent", label_id: "Tanpa consent riset", label_en: "No research consent" },
  { value: "manual", label_id: "Pengecualian manual", label_en: "Manual exclusion" },
  { value: "other", label_id: "Lainnya", label_en: "Other" },
];

const STORAGE_KEY = "se_responses_cols_v2";
const LEGACY_STORAGE_KEY = "se_responses_cols_v1";

function initialVisibleCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(saved) && saved.length) return new Set(saved);
    // Keep columns chosen before the language column existed, and show it.
    const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY));
    if (Array.isArray(legacy) && legacy.length) return new Set([...legacy, "language"]);
  } catch {}
  return new Set(ALL_COLUMNS.filter(c => c.default || c.sticky).map(c => c.key));
}

export default function AdminResponses() {
  const { lang } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [instruments, setInstruments] = useState([]);
  const [filters, setFilters] = useState({
    instrument_id: "all",
    status: "completed",
    excluded: "false",
    archived: "false",
    language: "all",
    q: "",
    date_from: "",
    date_to: "",
  });
  const [sort, setSort] = useState("-completed_at");
  const [selected, setSelected] = useState(new Set());
  const [openId, setOpenId] = useState(null);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeReason, setExcludeReason] = useState("duplicate");
  const [excludeNote, setExcludeNote] = useState("");
  const [permanentTarget, setPermanentTarget] = useState(null);
  const [permanentPhrase, setPermanentPhrase] = useState("");
  const [visibleCols, setVisibleCols] = useState(initialVisibleCols);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify([...visibleCols])); }, [visibleCols]);
  useEffect(() => { api.get("/admin/instruments").then(r => setInstruments(r.data)); }, []);

  const load = () => {
    const params = new URLSearchParams({
      page: String(page), limit: String(limit), sort,
      status: filters.status,
      excluded: filters.excluded,
      archived: filters.archived,
    });
    if (filters.instrument_id !== "all") params.set("instrument_id", filters.instrument_id);
    if (filters.language !== "all") params.set("language", filters.language);
    if (filters.q) params.set("q", filters.q);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    api.get(`/admin/responses?${params.toString()}`).then(r => setData(r.data));
  };
  useEffect(load, [page, limit, sort, filters.instrument_id, filters.status, filters.excluded, filters.archived, filters.language, filters.q, filters.date_from, filters.date_to]);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const cols = useMemo(() => ALL_COLUMNS.filter(c => visibleCols.has(c.key)), [visibleCols]);

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map(x => x.id)));
  };

  const doExport = (format, opts = {}) => {
    const token = localStorage.getItem("se_token");
    const params = new URLSearchParams({
      format,
      status: filters.status,
      excluded: filters.excluded,
      archived: filters.archived,
    });
    if (filters.instrument_id !== "all") params.set("instrument_id", filters.instrument_id);
    if (filters.language !== "all") params.set("language", filters.language);
    if (filters.q) params.set("q", filters.q);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (opts.include_items) params.set("include_items", "1");
    if (opts.long_format) params.set("long_format", "1");
    if (opts.de_identified) params.set("de_identified", "1");
    fetch(`${API_BASE}/admin/responses/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const suffix = opts.long_format ? "_long" : (opts.include_items ? "_items" : "");
        a.download = `responses_${Date.now()}${suffix}.${format}`;
        a.click();
        toast.success(lang === "id" ? "Ekspor selesai" : "Export ready");
      });
  };

  const submitExclude = async () => {
    if (!excludeTarget) return;
    await api.post(`/admin/responses/${excludeTarget}/exclude`, { reason: excludeReason, note: excludeNote });
    setExcludeTarget(null); setExcludeNote("");
    toast.success(lang === "id" ? "Dikecualikan dari analisis" : "Excluded from analysis");
    load();
  };
  const submitPermanent = async () => {
    if (!permanentTarget) return;
    try {
      await api.post(`/admin/responses/${permanentTarget}/permanent-delete`, { confirm_phrase: permanentPhrase });
      setPermanentTarget(null); setPermanentPhrase("");
      toast.success(lang === "id" ? "Terhapus permanen" : "Permanently deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const doAction = async (id, action) => {
    await api.post(`/admin/responses/${id}/${action}`);
    toast.success(lang === "id" ? "Berhasil" : "Done");
    load();
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    if (action === "permanent-delete" && !isAdmin) { toast.error("Admin only"); return; }
    const body = { session_ids: [...selected], action };
    if (action === "exclude") { body.reason = "manual"; }
    if (action === "permanent-delete") {
      const phrase = window.prompt(lang === "id" ? "Ketik HAPUS PERMANEN untuk konfirmasi" : "Type HAPUS PERMANEN to confirm");
      if (!phrase) return;
      body.confirm_phrase = phrase;
    }
    try {
      const r = await api.post("/admin/responses/bulk-action", body);
      toast.success(`${r.data.processed} ${lang === "id" ? "diproses" : "processed"}`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "Respons" : "Responses"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {lang === "id" ? `Total ${data.total} respons` : `${data.total} responses total`} — WIB (UTC+7)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="responses-columns-btn"><Columns3 className="w-4 h-4 mr-1" /> {lang === "id" ? "Kolom" : "Columns"}</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 max-h-96 overflow-y-auto">
              <div className="space-y-1.5">
                {ALL_COLUMNS.filter(c => c.key !== "select" && c.key !== "actions").map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary px-2 py-1 rounded">
                    <Checkbox
                      checked={visibleCols.has(c.key)}
                      onCheckedChange={(v) => {
                        const s = new Set(visibleCols);
                        if (v) s.add(c.key); else s.delete(c.key);
                        setVisibleCols(s);
                      }}
                      data-testid={`col-toggle-${c.key}`}
                    />
                    <span>{lang === "en" ? (c.labelEn || c.stickyEn || c.label) : c.label}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="responses-export-btn"><Download className="w-4 h-4 mr-1" /> {lang === "id" ? "Ekspor" : "Export"}</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-1 pb-0.5">{lang === "id" ? "Ringkasan" : "Summary"}</div>
              <button onClick={() => doExport("csv")} className="w-full text-left px-2 py-1.5 hover:bg-secondary rounded text-sm" data-testid="export-csv">CSV</button>
              <button onClick={() => doExport("xlsx")} className="w-full text-left px-2 py-1.5 hover:bg-secondary rounded text-sm" data-testid="export-xlsx">Excel (XLSX) · multi-sheet</button>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-2 pb-0.5">{lang === "id" ? "Level item" : "Item-level"}</div>
              <button onClick={() => doExport("xlsx", { include_items: true })} className="w-full text-left px-2 py-1.5 hover:bg-secondary rounded text-sm" data-testid="export-xlsx-items">Wide format (XLSX + items)</button>
              <button onClick={() => doExport("csv", { long_format: true, include_items: true })} className="w-full text-left px-2 py-1.5 hover:bg-secondary rounded text-sm" data-testid="export-csv-long">Long format (CSV, one answer/row)</button>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 pt-2 pb-0.5">{lang === "id" ? "De-identifikasi" : "De-identified"}</div>
              <button onClick={() => doExport("xlsx", { de_identified: true, include_items: true })} className="w-full text-left px-2 py-1.5 hover:bg-secondary rounded text-sm" data-testid="export-xlsx-deid">XLSX (tanpa nama/email)</button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 md:grid-cols-7 gap-2">
        <div className="col-span-2 md:col-span-2 relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input placeholder={lang === "id" ? "Cari kode / nama / email..." : "Search code / name / email..."} value={filters.q} onChange={(e) => { setFilters(f => ({...f, q: e.target.value})); setPage(1); }} className="pl-9 h-10" data-testid="responses-search" />
        </div>
        <Select value={filters.instrument_id} onValueChange={(v) => { setFilters(f => ({...f, instrument_id: v})); setPage(1); }}>
          <SelectTrigger className="h-10" data-testid="filter-instrument"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "id" ? "Semua instrumen" : "All instruments"}</SelectItem>
            {instruments.map(i => (<SelectItem key={i.id} value={i.id}>{lang === "id" ? i.name_id : i.name_en}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => { setFilters(f => ({...f, status: v})); setPage(1); }}>
          <SelectTrigger className="h-10" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "id" ? "Semua status" : "All statuses"}</SelectItem>
            <SelectItem value="completed">{lang === "id" ? "Selesai" : "Completed"}</SelectItem>
            <SelectItem value="in_progress">{lang === "id" ? "Berjalan" : "In progress"}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.excluded} onValueChange={(v) => { setFilters(f => ({...f, excluded: v})); setPage(1); }}>
          <SelectTrigger className="h-10" data-testid="filter-excluded"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "id" ? "Semua" : "All"}</SelectItem>
            <SelectItem value="false">{lang === "id" ? "Aktif" : "Included"}</SelectItem>
            <SelectItem value="true">Excluded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.archived} onValueChange={(v) => { setFilters(f => ({...f, archived: v})); setPage(1); }}>
          <SelectTrigger className="h-10" data-testid="filter-archived"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "id" ? "Semua" : "All"}</SelectItem>
            <SelectItem value="false">{lang === "id" ? "Belum diarsipkan" : "Not archived"}</SelectItem>
            <SelectItem value="true">{lang === "id" ? "Diarsipkan" : "Archived"}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.language} onValueChange={(v) => { setFilters(f => ({...f, language: v})); setPage(1); }}>
          <SelectTrigger className="h-10" data-testid="filter-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === "id" ? "Semua bahasa" : "All languages"}</SelectItem>
            <SelectItem value="id">{responseLanguageName("id", lang)}</SelectItem>
            <SelectItem value="en">{responseLanguageName("en", lang)}</SelectItem>
            <SelectItem value="switched">{lang === "id" ? "Berganti bahasa" : "Switched language"}</SelectItem>
            <SelectItem value="unknown">{lang === "id" ? "Tidak diketahui" : "Unknown"}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 p-3 rounded-xl bg-terracotta-50 border border-terracotta-200 flex items-center gap-2 flex-wrap" data-testid="bulk-bar">
          <span className="text-sm text-teal-800">{selected.size} {lang === "id" ? "dipilih" : "selected"}</span>
          <div className="ml-auto flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => bulkAction("exclude")} data-testid="bulk-exclude"><Ban className="w-3.5 h-3.5 mr-1" /> {lang === "id" ? "Kecualikan" : "Exclude"}</Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction("archive")} data-testid="bulk-archive"><Archive className="w-3.5 h-3.5 mr-1" /> {lang === "id" ? "Arsipkan" : "Archive"}</Button>
            <Button size="sm" variant="outline" onClick={() => bulkAction("delete")} data-testid="bulk-delete"><Trash2 className="w-3.5 h-3.5 mr-1" /> {lang === "id" ? "Hapus" : "Delete"}</Button>
            {isAdmin && <Button size="sm" variant="outline" onClick={() => bulkAction("permanent-delete")} className="text-destructive border-destructive" data-testid="bulk-permanent-delete">{lang === "id" ? "Hapus Permanen" : "Permanent Delete"}</Button>}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm min-w-full">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {cols.map((c) => {
                  const stickyCls = c.sticky === true ? "sticky left-0 bg-secondary z-10" : c.sticky === "right" ? "sticky right-0 bg-secondary z-10" : "";
                  const label = lang === "en" ? (c.labelEn || c.stickyEn || c.label) : c.label;
                  return (
                    <th key={c.key} className={`text-left p-3 ${stickyCls}`} style={{minWidth: c.width}}>
                      {c.key === "select" ? (
                        <Checkbox checked={selected.size > 0 && selected.size === data.items.length} onCheckedChange={toggleAll} data-testid="select-all" />
                      ) : label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.items.map((s) => (
                <tr key={s.id} data-testid={`response-row-${s.anonymous_code}`} className={s.is_excluded ? "bg-cream-100/60" : ""}>
                  {cols.map((c) => {
                    const stickyCls = c.sticky === true ? "sticky left-0 bg-card z-10" : c.sticky === "right" ? "sticky right-0 bg-card z-10" : "";
                    return (
                      <td key={c.key} className={`p-3 ${stickyCls}`}>
                        {renderCell(c.key, s, { lang, setOpenId, doAction, setExcludeTarget, setPermanentTarget, isAdmin, selected, toggleSel })}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={cols.length} className="p-10 text-center text-muted-foreground">{lang === "id" ? "Tidak ada respons yang cocok." : "No matching responses."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{lang === "id" ? "Per halaman:" : "Per page:"}</span>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="h-8 w-20" data-testid="limit-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 25, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span data-testid="page-indicator">{page} / {totalPages}</span>
          <Button variant="outline" size="icon" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {openId && <ResponseDetail id={openId} onClose={() => setOpenId(null)} />}

      <Dialog open={!!excludeTarget} onOpenChange={(v) => { if (!v) setExcludeTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{lang === "id" ? "Kecualikan dari Analisis" : "Exclude from Analysis"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Alasan" : "Reason"}</Label>
            <Select value={excludeReason} onValueChange={setExcludeReason}>
              <SelectTrigger data-testid="exclude-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{EXCLUDE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{lang === "id" ? r.label_id : r.label_en}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Catatan (opsional)" : "Note (optional)"}</Label>
            <Textarea value={excludeNote} onChange={(e) => setExcludeNote(e.target.value)} rows={3} data-testid="exclude-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcludeTarget(null)}>{lang === "id" ? "Batal" : "Cancel"}</Button>
            <Button onClick={submitExclude} className="bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="exclude-submit">{lang === "id" ? "Kecualikan" : "Exclude"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!permanentTarget} onOpenChange={(v) => { if (!v) { setPermanentTarget(null); setPermanentPhrase(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> {lang === "id" ? "Hapus Permanen" : "Permanent Delete"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">{lang === "id" ? "Tindakan ini tidak dapat dibatalkan. Data respon akan hilang secara permanen dari database, dan mungkin memengaruhi norm yang menggunakannya." : "This cannot be undone. The response will be permanently removed from the database, and may impact norms built from it."}</p>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Ketik HAPUS PERMANEN" : "Type HAPUS PERMANEN"}</Label>
            <Input value={permanentPhrase} onChange={(e) => setPermanentPhrase(e.target.value)} data-testid="permanent-confirm" className="font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermanentTarget(null)}>{lang === "id" ? "Batal" : "Cancel"}</Button>
            <Button onClick={submitPermanent} disabled={permanentPhrase !== "HAPUS PERMANEN"} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="permanent-submit">{lang === "id" ? "Hapus Permanen" : "Permanent Delete"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function renderCell(key, s, { lang, setOpenId, doAction, setExcludeTarget, setPermanentTarget, isAdmin, selected, toggleSel }) {
  const demo = s.demographics || {};
  const rs = s.result_summary || {};
  switch (key) {
    case "select": return <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleSel(s.id)} data-testid={`select-${s.anonymous_code}`} />;
    case "anonymous_code": return <span className="font-mono text-xs">{s.anonymous_code}</span>;
    case "instrument_name_id": return <div><div className="font-medium">{lang === "id" ? s.instrument_name_id : (s.instrument_name_en || s.instrument_name_id)}</div><div className="text-xs text-muted-foreground">{s.instrument_slug}</div></div>;
    case "instrument_version": return s.instrument_version ? `v${s.instrument_version}` : "—";
    case "status":
      return <Badge variant="outline" className={s.status === "completed" ? "border-sage-300 bg-sage-50 text-teal-800" : "border-border"}>{s.status}</Badge>;
    case "language":
      return s.language
        ? <Badge variant="outline" className={s.language_switched ? "border-terracotta-300 bg-terracotta-50 text-terracotta-700" : "border-border"} title={s.language_switched ? (lang === "id" ? "Berganti bahasa saat mengisi" : "Switched language while filling") : undefined} data-testid={`response-language-${s.anonymous_code}`}>{responseLanguageLabel(s, lang)}</Badge>
        : <span className="text-muted-foreground text-xs" data-testid={`response-language-${s.anonymous_code}`}>{responseLanguageLabel(s, lang)}</span>;
    case "name": return demo.name || "—";
    case "email": return demo.email || "—";
    case "age": return demo.age || "—";
    case "gender": return demo.gender || "—";
    case "education": return demo.education || "—";
    case "occupation": return demo.occupation || "—";
    case "province": return demo.province || "—";
    case "city": return demo.city || "—";
    case "started_at": return <span className="text-xs">{fmtWIB(s.started_at, lang)}</span>;
    case "completed_at": return <span className="text-xs">{fmtWIB(s.completed_at, lang)}</span>;
    case "duration_seconds": return <span className="text-xs">{fmtDuration(s.duration_seconds)}</span>;
    case "total_score": return rs.total_score ?? "—";
    case "overall_category": return rs.overall_category || "—";
    case "dominant_dimension": return rs.dominant_dimension || "—";
    case "is_excluded": return s.is_excluded ? <Badge variant="outline" className="border-terracotta-300 bg-terracotta-50 text-terracotta-700">Excluded</Badge> : <span className="text-muted-foreground text-xs">—</span>;
    case "is_archived": return s.is_archived ? <Badge variant="outline">Arsip</Badge> : <span className="text-muted-foreground text-xs">—</span>;
    case "actions": return (
      <div className="flex items-center gap-1 justify-end">
        <Button size="icon" variant="ghost" onClick={() => setOpenId(s.id)} data-testid={`response-view-${s.anonymous_code}`} title="Detail"><Eye className="w-4 h-4" /></Button>
        {s.is_excluded ? (
          <Button size="icon" variant="ghost" onClick={() => doAction(s.id, "unexclude")} data-testid={`response-unexclude-${s.anonymous_code}`} title="Include"><RotateCcw className="w-4 h-4" /></Button>
        ) : (
          <Button size="icon" variant="ghost" onClick={() => setExcludeTarget(s.id)} data-testid={`response-exclude-${s.anonymous_code}`} title="Exclude"><Ban className="w-4 h-4" /></Button>
        )}
        {s.is_archived ? (
          <Button size="icon" variant="ghost" onClick={() => doAction(s.id, "restore")} data-testid={`response-restore-${s.anonymous_code}`} title="Restore"><RotateCcw className="w-4 h-4" /></Button>
        ) : (
          <Button size="icon" variant="ghost" onClick={() => doAction(s.id, "archive")} data-testid={`response-archive-${s.anonymous_code}`} title="Archive"><Archive className="w-4 h-4" /></Button>
        )}
        {isAdmin && (
          <Button size="icon" variant="ghost" onClick={() => setPermanentTarget(s.id)} className="text-destructive" data-testid={`response-permdelete-${s.anonymous_code}`} title="Permanent delete"><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>
    );
    default: return "—";
  }
}
